import { useEffect, useMemo, useState } from "react";
import {
	generateBiomeCanvasDataUrl,
	generateFogWave,
	generateMountainOverlayDataUrl,
	getSmoothPath,
	renderDecorShape,
} from "./mapUtils";
import { createNoise2D } from "simplex-noise";
import alea from "alea";

const biomeNoise = createNoise2D(alea("biome-seed"));

const ProgressMapEditor = () => {
	const [mode, setMode] = useState("visualizer"); // "editor" or "visualizer"
	const [brushType, setBrushType] = useState("path"); // "path", "tree", "rock", "house"

	const [customPoints, setCustomPoints] = useState([
		{
			x: 15,
			y: 100,
		},
	]);
	const [decor, setDecor] = useState([]);

	const [justDragged, setJustDragged] = useState(false);
	const [draggedIndex, setDraggedIndex] = useState(null);
	const [draggedDecorIndex, setDraggedDecorIndex] = useState(null);

	const [biomeDataUrl, setBiomeDataUrl] = useState(null);
	const [mountainOverlayUrl, setMountainOverlayUrl] = useState(null);

	const mapWidth = useMemo(() => {
		const maxPointX = Math.max(...customPoints.map((p) => p.x), 0) + 50;
		return Math.max(window.innerWidth, maxPointX);
	}, [customPoints]);

	const playerIndex = 6;
	const fogLimitIndex = Math.max(playerIndex + 50, customPoints.length - 1);

	useEffect(() => {
		console.log("mapWidth", mapWidth);
		const biomeUrl = generateBiomeCanvasDataUrl(mapWidth, 200, biomeNoise);
		const overlayUrl = generateMountainOverlayDataUrl(
			mapWidth,
			200,
			biomeNoise
		);
		setBiomeDataUrl(biomeUrl);
		setMountainOverlayUrl(overlayUrl);
	}, [mapWidth]);

	useEffect(() => {
		const maxPointX = Math.max(...customPoints.map((p) => p.x), 0) + 50;
		const newMapWidth = Math.max(window.innerWidth, maxPointX);

		if (newMapWidth === mapWidth) return;

		const biomeUrl = generateBiomeCanvasDataUrl(newMapWidth, 200, biomeNoise);
		const overlayUrl = generateMountainOverlayDataUrl(
			mapWidth,
			200,
			biomeNoise
		);
		setBiomeDataUrl(biomeUrl);
		setMountainOverlayUrl(overlayUrl);
	}, [customPoints]);

	const handleEditorClick = (e) => {
		if (mode !== "editor") return;

		if (justDragged) {
			setJustDragged(false);
			return;
		}

		const svg = e.currentTarget;
		const pt = svg.createSVGPoint();
		pt.x = e.clientX;
		pt.y = e.clientY;
		const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

		const point = { x: svgP.x, y: svgP.y, type: brushType };

		if (brushType === "path") {
			setCustomPoints([...customPoints, point]);
		} else {
			setDecor([...decor, point]);
		}
	};

	const handleDeleteDecor = (index) => {
		setDecor((prev) => prev.filter((_, i) => i !== index));
	};

	const handleUndo = () => {
		switch (brushType) {
			case "path":
				setCustomPoints((prev) => prev.slice(0, -1));
				break;
			default:
				setDecor((prev) => {
					const lastIndex = [...prev]
						.reverse()
						.findIndex((d) => d.type === brushType);
					if (lastIndex === -1) return prev;
					const indexToRemove = prev.length - 1 - lastIndex;
					return prev.filter((_, i) => i !== indexToRemove);
				});
				break;
		}
	};

	const handleSave = () => {
		const saveData = {
			path: customPoints,
			decor,
		};

		const blob = new Blob([JSON.stringify(saveData, null, 2)], {
			type: "application/json",
		});

		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.download = "map-data.json";
		link.click();

		URL.revokeObjectURL(link.href);
	};

	const handleMouseMove = (e) => {
		if (draggedIndex === null && draggedDecorIndex === null) return;

		const svg = document.querySelector(".scrollable-map-svg");
		const pt = svg.createSVGPoint();
		pt.x = e.clientX;
		pt.y = e.clientY;
		const { x, y } = pt.matrixTransform(svg.getScreenCTM().inverse());

		if (draggedIndex !== null) {
			setCustomPoints((prev) =>
				prev.map((p, idx) => (idx === draggedIndex ? { ...p, x, y } : p))
			);
		}

		if (draggedDecorIndex !== null) {
			setDecor((prev) =>
				prev.map((p, idx) => (idx === draggedDecorIndex ? { ...p, x, y } : p))
			);
		}
	};

	const handleMouseUp = () => {
		if (draggedIndex !== null || draggedDecorIndex !== null) {
			setDraggedIndex(null);
			setDraggedDecorIndex(null);
			setJustDragged(true);
		}
	};

	useEffect(() => {
		window.addEventListener("mousemove", handleMouseMove);
		window.addEventListener("mouseup", handleMouseUp);
		return () => {
			window.removeEventListener("mousemove", handleMouseMove);
			window.removeEventListener("mouseup", handleMouseUp);
		};
	}, [draggedIndex, draggedDecorIndex]);

	const handleFileUpload = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const data = JSON.parse(e.target.result);
				setCustomPoints(data.path || []);
				setDecor(data.decor || []);
			} catch (err) {
				alert("Invalid JSON file.");
			}
		};
		reader.readAsText(file);
	};

	const pathD = getSmoothPath(customPoints);

	const groupedBrushTypes = {
		Path: ["path"],
		Trees: ["tree", "tree_1", "tree_2", "bush_1"],
		Rocks: ["pebbles", "small-rock", "big-rock"],
		Buildings: [
			"batiment_1",
			"batiment_2",
			"batiment_3",
			"batiment_4",
			"batiment_5",
		],
		Flags: [
			"confogaz",
			"elonova",
			"eopur",
			"filacom",
			"info_tec_services",
			"iserba",
			"reso_confort",
			"sanilogis",
			"sevanova",
			"smecso",
			"tekinova",
			"unica",
			"vizavy",
		],
	};

	return (
		<div className="scrollable-map-container">
			<div className="fixed flex items-end top-4 left-4 bg-[#fff] gap-2 p-2">
				<button
					className="bg-black rounded-md text-white text-sm px-2 py-1"
					onClick={() => setMode(mode === "editor" ? "visualizer" : "editor")}
				>
					{mode === "editor" ? "Visualizer" : "Editor"}
				</button>
				<div className="text-sm">
					pts count: <br />
					{customPoints.length}
				</div>
				{mode === "editor" && (
					<>
						<input
							type="file"
							accept=".json"
							onChange={handleFileUpload}
							className="ml-2 w-44 flex flex-col"
						/>

						<div className="flex gap-4 items-center mr-2 ml-2">
							{Object.entries(groupedBrushTypes).map(([groupName, types]) => (
								<div key={groupName} className="flex flex-col items-start">
									<label className="text-xs text-gray-700">{groupName}</label>
									<select
										className="px-2 py-1 rounded-md bg-slate-500 text-white text-sm"
										value={types.includes(brushType) ? brushType : ""}
										onChange={(e) => setBrushType(e.target.value)}
									>
										<option value="" disabled>
											Select
										</option>
										{types.map((type) => (
											<option key={type} value={type}>
												{type}
											</option>
										))}
									</select>
								</div>
							))}
						</div>

						<button
							className="bg-black rounded-md text-sm text-white px-2 py-1"
							onClick={handleUndo}
						>
							Undo
						</button>
						<button
							className="bg-black rounded-md text-sm text-white px-2 py-1"
							onClick={handleSave}
						>
							Save
						</button>
					</>
				)}
			</div>
			<svg
				className="scrollable-map-svg"
				viewBox={`0 0 ${mapWidth} 200`}
				xmlns="http://www.w3.org/2000/svg"
				style={{
					background: "#eee",
					cursor: mode === "editor" ? "crosshair" : "default",
				}}
				onClick={handleEditorClick}
			>
				{/* Biomes */}
				{biomeDataUrl && (
					<image
						href={biomeDataUrl}
						x="0"
						y="0"
						width={mapWidth}
						height={200}
					/>
				)}

				{/* Path */}
				{/* Cartoon-style outline */}
				{mode === "visualizer" && (
					<path
						d={pathD}
						fill="none"
						stroke="#D6C397" // dark brown or black
						strokeWidth="10"
						strokeLinejoin="round"
						strokeLinecap="round"
					/>
				)}

				{/* Main path */}
				<path
					d={pathD}
					fill="none"
					stroke="#C2A877"
					strokeWidth="6"
					strokeLinejoin="round"
					strokeLinecap="round"
				/>

				{/* Checkpoints */}
				{customPoints.map((p, i) => (
					<circle key={i} cx={p.x} cy={p.y} r="2" fill="#9C865F" />
				))}

				{mountainOverlayUrl && mode === "visualizer" && (
					<image
						href={mountainOverlayUrl}
						x="0"
						y="0"
						width={mapWidth}
						height={200}
					/>
				)}

				{decor
					.map((d, i) => ({ ...d, originalIndex: i }))
					.sort((a, b) => a.y - b.y)
					.map((d, i) => (
						<>
							<g key={d.originalIndex}>
								{renderDecorShape(d)}

								{mode === "editor" && (
									<g>
										<g
											onClick={(e) => {
												e.stopPropagation();
												handleDeleteDecor(d.originalIndex);
											}}
											style={{ cursor: "pointer" }}
										>
											<rect
												x={d.x - 2}
												y={d.y - 14}
												width={4}
												height={4}
												fill="white"
												stroke="red"
												strokeWidth={0.5}
												rx={0.5}
											/>
											<line
												x1={d.x - 1}
												y1={d.y - 13}
												x2={d.x + 1}
												y2={d.y - 11}
												stroke="red"
												strokeWidth={0.75}
											/>
											<line
												x1={d.x + 1}
												y1={d.y - 13}
												x2={d.x - 1}
												y2={d.y - 11}
												stroke="red"
												strokeWidth={0.75}
											/>
										</g>
										{/* Drag Handle */}
										<circle
											cx={d.x + 6}
											cy={d.y - 12}
											r={2}
											fill="black"
											onMouseDown={(e) => {
												e.stopPropagation();
												setDraggedDecorIndex(d.originalIndex);
											}}
											style={{ cursor: "grab" }}
										/>
									</g>
								)}
							</g>
						</>
					))}

				{/* Path points (for editor mode) */}
				{mode === "editor" &&
					customPoints.map((p, i) => (
						<g key={i}>
							{/* Main point visual */}
							<circle cx={p.x} cy={p.y} r={5} fill="blue" />

							{/* Delete Button */}
							<g
								onClick={(e) => {
									e.stopPropagation();
									setCustomPoints((prev) => prev.filter((_, idx) => idx !== i));
								}}
								style={{ cursor: "pointer" }}
							>
								<rect
									x={p.x - 2}
									y={p.y - 12}
									width={4}
									height={4}
									fill="white"
									stroke="red"
									strokeWidth={0.5}
									rx={0.5}
								/>
								<line
									x1={p.x - 1}
									y1={p.y - 11}
									x2={p.x + 1}
									y2={p.y - 9}
									stroke="red"
									strokeWidth={0.75}
								/>
								<line
									x1={p.x + 1}
									y1={p.y - 11}
									x2={p.x - 1}
									y2={p.y - 9}
									stroke="red"
									strokeWidth={0.75}
								/>
							</g>

							{/* Drag Handle */}
							<circle
								cx={p.x + 6}
								cy={p.y - 10}
								r={2}
								fill="black"
								onMouseDown={(e) => {
									e.stopPropagation();
									setDraggedIndex(i);
								}}
								style={{ cursor: "grab" }}
							/>
						</g>
					))}

				{/* Player marker (only in visualizer) */}
				{mode === "visualizer" && customPoints[playerIndex] && (
					<circle
						cx={customPoints[playerIndex].x}
						cy={customPoints[playerIndex].y}
						r="6"
						fill="#2196F3"
					/>
				)}
				{/* Fog (with wavy left edge) */}
				{mode === "visualizer" && (
					<>
						<path
							d={generateFogWave(
								customPoints[fogLimitIndex]?.x || window.innerWidth - 300,
								mapWidth
							)}
							fill="white"
							opacity="0.65"
						/>
						<path
							d={generateFogWave(
								customPoints[fogLimitIndex]?.x + 20 || window.innerWidth - 280,
								mapWidth,
								200,
								10,
								15
							)}
							fill="white"
							opacity="0.75"
						/>
						<path
							d={generateFogWave(
								customPoints[fogLimitIndex]?.x + 50 || window.innerWidth - 250,
								mapWidth,
								200,
								5,
								20
							)}
							fill="white"
							opacity="0.85"
						/>
					</>
				)}
			</svg>
		</div>
	);
};

export default ProgressMapEditor;
